import React, { useEffect, useState } from 'react';

import { useMediaQuery } from '@material-ui/core';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { dateObject } from 'utils';
import { decimalToPercent } from 'utils/converter';

// Components
import { Mui, MuiLab, TextField } from 'components';
import { Workflow } from 'components/common';
import { Stack } from 'components/layouts';

// Local components
import EarlyPaymentDiscounts from '../EarlyPaymentDiscounts';

const PAYMENT_PLANS = [
  { label: 'Selecione um plano de pagamento', value: '' },
  { label: 'Mensal', value: 'monthly' },
  { label: 'Parcela única', value: 'single-installment' },
  { label: 'Customizado', value: 'custom' },
];

const FORM_MESSAGES = {
  name: { label: 'Nome', required: 'É necessário informar o nome' },
  installmentAmount: { label: 'Valor da parcela' },
  originalAmount: {
    label: 'Valor integral da negociação',
    required: 'É necessário informar o valor integral da negociação',
  },
  dueDay: {
    label: 'Dia do vencimento da(s) parcela(s)',
    min: 'Dia deve ser maior ou igual a 1',
    max: 'Dia deve ser menor ou igual a 30',
    required: 'É necessário informar o dia de vencimento',
  },
  dueDaySelectionEnabled: { label: 'Permitir que o responsável escolha o dia do vencimento' },
  cycle: { label: 'Ciclo' },
  paymentPlan: {
    label: 'Plano de pagamento',
    required: 'É necessário selecionar um plano de pagamento',
  },
  recurrence: {
    label: 'Quantidade de parcelas',
    min: 'Mínimo 1 parcela',
    helperText: 'Quantidade de parcelas que serão geradas',
  },
  recurrenceSelectionEnabled: {
    label: 'Permitir que o responsável escolha a quantidade de parcelas',
  },
  billingStartDate: {
    label: 'Data para começar a gerar os pagamentos',
    helperText: 'Informe a data para iniciar a geração da(s) cobrança(s).',
    required: 'É necessário informar a data para começar a gerar os pagamentos',
  },
  billingStartDateType: { label: 'Origem da data de início da cobrança' },
  monthlyInterestRate: { label: 'Taxa de juros mensal' },
  latePaymentFinePercentage: { label: 'Multa por atraso percentual' },
  latePaymentFineFixed: { label: 'Multa por atraso fixa' },
  payableWith: { label: 'Métodos de pagamento disponíveis' },
  maxInstallments: {
    label: 'Máximo de parcelas',
    required: 'É necessário preencher o máximo de parcelas',
    min: 'Valor mínimo para o máximo de parcelas deve ser 1',
  },
  description: {
    label: 'Descrição dos pagamentos',
    helperText: 'Informações adicionais a serem incluídas nos pagamentos',
  },
  isActive: { label: 'Ativo?' },
  earlyPaymentDiscounts: {
    days: {
      label: 'Dias',
      required: 'É necessário preencher os dias antes do vencimento do desconto',
    },
    fixedAmount: { label: 'Valor', required: 'É necessário preencher o valor' },
    percentageAmount: { label: 'Percentual', required: 'É necessário preencher o campo' },
  },
};

function Section({ children, isEditView }) {
  if (!children) return null;
  if (isEditView) return <Mui.Box>{children}</Mui.Box>;
  return (
    <Mui.Card variant="outlined">
      <Mui.CardContent>{children}</Mui.CardContent>
    </Mui.Card>
  );
}

Section.propTypes = {
  children: PropTypes.node,
  isEditView: PropTypes.bool,
};

function Create({
  isEditView,
  values,
  onFinish,
  onRemoveDiscount,
  onSubmit,
  CardSelector,
  CycleSelect,
  PaymentsView,
  ProductsView,
}) {
  // Hooks
  const isSm = useMediaQuery(theme => theme.breakpoints.down('sm'));

  // States
  const [step, setStep] = useState(0);
  const [discountsEnabled, setDiscountsEnabled] = useState(false);

  // Constants
  const initialValues = {
    name: values?.name || '',
    installmentAmount: values?.originalAmount
      ? (Number(values.originalAmount) / Number(values?.recurrence || 1)).toFixed(2)
      : '',
    originalAmount: values?.originalAmount || '',
    dueDay: values?.dueDay || 5,
    dueDaySelectionEnabled: values?.dueDaySelectionEnabled || false,
    cycle: values?.cycle || new Date().getFullYear(),
    paymentPlan: values?.paymentPlan || '',
    recurrence: values?.recurrence || 1,
    recurrenceSelectionEnabled: values?.recurrenceSelectionEnabled || false,
    billingStartDate: values?.billingStartDate ? dateObject(values.billingStartDate) : '',
    billingStartDateType: values?.billingStartDateType || 'fixed',
    monthlyInterestRate: Number(values?.monthlyInterestRate)
      ? decimalToPercent(values.monthlyInterestRate)
      : 0,
    latePaymentFinePercentage: Number(values?.latePaymentFinePercentage)
      ? decimalToPercent(values.latePaymentFinePercentage)
      : 0,
    latePaymentFineFixed: values?.latePaymentFineFixed || 0,
    payableWith: values?.payableWith || [],
    maxInstallments: values?.maxInstallments || 1,
    description: values?.description || '',
    isActive: values ? values.isActive : true,
    /* eslint-disable indent */
    earlyPaymentDiscounts: values?.earlyPaymentDiscounts
      ? values.earlyPaymentDiscounts.map(earlyPaymentDiscount => ({
          ...earlyPaymentDiscount,
          percentageAmount: Number(earlyPaymentDiscount.percentageAmount)
            ? decimalToPercent(earlyPaymentDiscount.percentageAmount)
            : 0,
        }))
      : [],
    /* eslint-enable indent */
  };

  const discountSchema = Yup.object().shape({
    days: Yup.number()
      .min(0, FORM_MESSAGES.earlyPaymentDiscounts.days.required)
      .required(FORM_MESSAGES.earlyPaymentDiscounts.days.required),
    fixedAmount: Yup.number().when('percentageAmount', {
      is: percentageAmount => percentageAmount === 0,
      then: schema => schema.min(0.01, FORM_MESSAGES.earlyPaymentDiscounts.fixedAmount.required),
      otherwise: schema => schema.nullable(),
    }),
    percentageAmount: Yup.number().nullable(),
  });
  const yup = Yup.object().shape({
    name: Yup.string().required(FORM_MESSAGES.name.required),
    originalAmount: Yup.string().required(FORM_MESSAGES.originalAmount.required),
    dueDay: Yup.number()
      .min(1, FORM_MESSAGES.dueDay.min)
      .max(30, FORM_MESSAGES.dueDay.max)
      .required(FORM_MESSAGES.dueDay.required),
    dueDaySelectionEnabled: Yup.boolean(),
    cycle: Yup.number().nullable(),
    paymentPlan: Yup.string().nullable().required(FORM_MESSAGES.paymentPlan.required),
    recurrence: Yup.number().min(1, FORM_MESSAGES.recurrence.min),
    recurrenceSelectionEnabled: Yup.boolean(),
    billingStartDate: Yup.date()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .when(['billingStartDateType', 'paymentPlan'], {
        is: (billingStartDateType, paymentPlan) =>
          billingStartDateType === 'fixed' && paymentPlan !== 'custom',
        then: schema => schema.required(FORM_MESSAGES.billingStartDate.required),
        otherwise: schema => schema.nullable(),
      }),
    billingStartDateType: Yup.string().nullable(),
    monthlyInterestRate: Yup.number().nullable(),
    latePaymentFinePercentage: Yup.number().nullable(),
    latePaymentFineFixed: Yup.number().nullable(),
    payableWith: Yup.array().nullable(),
    maxInstallments: Yup.number().when(['paymentPlan', 'payableWith'], {
      is: (paymentPlan, payableWith) =>
        paymentPlan === 'single-installment' && payableWith?.includes('credit_card'),
      then: schema =>
        schema
          .nullable()
          .min(1, FORM_MESSAGES.maxInstallments.min)
          .required(FORM_MESSAGES.maxInstallments.required),
      otherwise: schema => schema.nullable(),
    }),
    description: Yup.string().nullable(),
    isActive: Yup.boolean(),
    earlyPaymentDiscounts: Yup.array().of(discountSchema).nullable(),
  });

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: yup,
    enableReinitialize: true,
  });

  const step1Filled = !!formik.values.name && !!formik.values.paymentPlan;
  const step2Filled =
    step1Filled &&
    !!formik.values.originalAmount &&
    !!formik.values.dueDay &&
    (!formik.values.payableWith?.includes('credit_card') ||
      (!!formik.values.payableWith?.includes('credit_card') && !!formik.values.maxInstallments));

  // Actions
  const showStep = index => step === index;

  const checkStepFilled = () => {
    switch (step) {
      case 0:
        return step1Filled;
      case 1:
        return step2Filled;
      case 2:
        return step2Filled;
      case 3:
        return step2Filled;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (!checkStepFilled()) return;
    if (!values && step === 2) {
      if (!formik.isValid) return;
      await formik.handleSubmit();
    }
    setStep(step + 1);
  };

  const handleCheckPayableWith = (paymentMethod, checked) => {
    const selectedPayableWith = structuredClone(formik.values.payableWith);
    formik.setFieldTouched('payableWith', true);
    if (checked) {
      selectedPayableWith.push(paymentMethod);
      formik.setFieldValue('payableWith', [...selectedPayableWith], true);
    } else {
      formik.setFieldValue(
        'payableWith',
        selectedPayableWith.filter(item => item !== paymentMethod),
        true,
      );
    }
  };

  const handleChangePaymentPlan = e => {
    const { value } = e.target;
    formik.setValues(
      {
        ...formik.values,
        paymentPlan: value,
        installmentAmount: '',
        maxInstallments: '',
        recurrence: 1,
        ...(value === 'custom' ? { billingStartDate: '', dueDay: 1 } : {}),
      },
      true,
    );
  };

  const steps = [
    {
      key: 0,
      label: 'Dados gerais',
      icon: 'info',
      active: step === 0,
      error: !isEditView && !step1Filled,
      onClick: () => setStep(0),
    },
    {
      key: 1,
      label: 'Valores e pagamento',
      icon: 'payments',
      active: step === 1,
      error: !isEditView && !step2Filled,
      onClick: step1Filled ? () => setStep(1) : undefined,
    },
    {
      key: 2,
      label: 'Descontos de pontualidade',
      icon: 'local_offer',
      active: step === 2,
      onClick: step2Filled ? () => setStep(2) : undefined,
    },
    {
      key: 3,
      label:
        formik.values.paymentPlan === 'custom' ? 'Pagamentos customizados' : 'Taxa de matrícula',
      icon: 'receipt',
      active: step === 3,
      onClick: formik.values.paymentPlan ? () => setStep(3) : undefined,
      hidden: !PaymentsView,
    },
    {
      key: 4,
      label: 'Produtos',
      icon: 'shopping_cart',
      active: step === 4,
      onClick: step2Filled ? () => setStep(4) : undefined,
      hidden: !ProductsView,
    },
  ].filter(item => !item.hidden);

  const showMaxInstallments =
    formik.values.paymentPlan === 'single-installment' &&
    formik.values.payableWith.includes('credit_card');
  const showBillingStartDate =
    formik.values.paymentPlan &&
    formik.values.paymentPlan !== 'custom' &&
    formik.values.billingStartDateType === 'fixed';
  const showDiscounts =
    isEditView || (!isEditView && discountsEnabled) || !!formik.values.earlyPaymentDiscounts.length;
  const showSubmitInEditView = isEditView && step < 3;
  const showSubmitInRegisterView =
    !isEditView && ((!values && step === steps.length - 1) || (!!values && step < 3));
  const showSubmitButton = showSubmitInEditView || showSubmitInRegisterView;

  useEffect(() => {
    if (showBillingStartDate) formik.setFieldTouched('billingStartDate', true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBillingStartDate]);

  return (
    <Stack>
      {isEditView ? (
        <>
          <Mui.Tabs
            value={step}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto">
            {steps.map(item => (
              <Mui.Tab
                key={item.key}
                label={item.label}
                icon={<Mui.Icon>{item.icon}</Mui.Icon>}
                onClick={item.onClick}
              />
            ))}
          </Mui.Tabs>
          <Mui.Divider />
        </>
      ) : (
        <Workflow.Base steps={steps} activeStep={step} alternativeLabel />
      )}
      <Section isEditView={isEditView}>
        {showStep(0) && (
          <Stack>
            {!isEditView && (
              <Mui.Box>
                <Mui.Typography variant={isSm ? 'h5' : 'h4'}>
                  Configure o modelo de negociação
                </Mui.Typography>
                <Mui.Typography variant="body1">
                  Defina as informações do modelo de negociação.
                </Mui.Typography>
              </Mui.Box>
            )}
            <TextField
              required
              name="name"
              type="text"
              placeholder={FORM_MESSAGES.name.label}
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
            />
            <Stack
              layout={[
                { xs: 12, sm: 8 },
                { xs: 12, sm: 4 },
              ]}>
              <Mui.FormControl variant="outlined" fullWidth disabled={formik.isSubmitting}>
                <Mui.InputLabel id="paymentPlanLabel">
                  {FORM_MESSAGES.paymentPlan.label}
                </Mui.InputLabel>
                <Mui.Select
                  required
                  labelId="paymentPlanLabel"
                  label={FORM_MESSAGES.paymentPlan.label}
                  id="paymentPlan"
                  value={formik.values.paymentPlan || []}
                  disabled={formik.isSubmitting}
                  onChange={handleChangePaymentPlan}>
                  {PAYMENT_PLANS.map(option => (
                    <Mui.MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </Mui.MenuItem>
                  ))}
                </Mui.Select>
              </Mui.FormControl>
              {!!CycleSelect &&
                CycleSelect({
                  formik,
                  name: 'cycle',
                  showAll: false,
                  disabled: formik.isSubmitting,
                  onChange: event => {
                    formik.setFieldTouched('cycle', true);
                    formik.setFieldValue('cycle', event.target.value);
                  },
                  onBlur: () => formik.setFieldTouched('cycle', true),
                })}
            </Stack>
            <TextField
              multiline
              rows={4}
              name="description"
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
            />
            <Mui.FormControlLabel
              control={
                <Mui.Switch
                  color="primary"
                  checked={!!formik.values.isActive}
                  onChange={event => {
                    formik.setFieldTouched('isActive', true);
                    formik.setFieldValue('isActive', event.target.checked, true);
                  }}
                  disabled={formik.isSubmitting}
                />
              }
              label={formik.values.isActive ? 'Ativo' : 'Inativo'}
            />
          </Stack>
        )}
        {showStep(1) && (
          <Stack>
            {!isEditView && (
              <Mui.Box>
                <Mui.Typography variant={isSm ? 'h5' : 'h4'}>
                  Quais valores serão cobrados para novas negociações?
                </Mui.Typography>
                <Mui.Typography variant="body1">
                  Os valores do modelo de negociação serão aplicados automaticamente às novas
                  negociações.
                </Mui.Typography>
              </Mui.Box>
            )}
            <Stack
              layout={[
                { xs: 12, sm: 6 },
                { xs: 12, sm: 6 },
                { xs: 12, sm: 6 },
                { xs: 12, sm: 6 },
                { xs: 12, sm: 6 },
              ]}>
              {formik.values.paymentPlan === 'monthly' && (
                <TextField
                  name="recurrence"
                  type="number"
                  formik={formik}
                  messages={FORM_MESSAGES}
                  disabled={formik.isSubmitting}
                  margin="none"
                  inputProps={{ min: 1 }}
                  onChange={e => {
                    const { value } = e.target;
                    formik.setFieldTouched('recurrence', true);
                    formik.setFieldValue('recurrence', value, true);

                    if (formik.values.installmentAmount > 0) {
                      formik.setFieldValue(
                        'originalAmount',
                        Number(
                          (Number(value) * Number(formik.values.installmentAmount)).toFixed(2),
                        ),
                      );
                    }
                  }}
                />
              )}
              {formik.values.paymentPlan === 'monthly' && (
                <Mui.FormControlLabel
                  control={
                    <Mui.Switch
                      color="primary"
                      checked={!!formik.values.recurrenceSelectionEnabled}
                      onChange={event => {
                        formik.setFieldTouched('recurrenceSelectionEnabled', true);
                        formik.setFieldValue('recurrenceSelectionEnabled', event.target.checked);
                      }}
                      disabled={formik.isSubmitting}
                    />
                  }
                  label={FORM_MESSAGES.recurrenceSelectionEnabled.label}
                />
              )}
              {formik.values.paymentPlan === 'monthly' && (
                <TextField
                  name="installmentAmount"
                  envelope="currency"
                  placeholder={FORM_MESSAGES.installmentAmount.label}
                  formik={formik}
                  messages={FORM_MESSAGES}
                  disabled={formik.isSubmitting}
                  margin="none"
                  onChange={e => {
                    const { value } = e.target;
                    formik.setFieldTouched('installmentAmount', true);
                    formik.setFieldValue('installmentAmount', value, true);

                    if (formik.values.recurrence > 0) {
                      formik.setFieldValue(
                        'originalAmount',
                        Number((Number(value) * Number(formik.values.recurrence)).toFixed(2)),
                      );
                    }
                  }}
                />
              )}
              <TextField
                required
                name="originalAmount"
                envelope="currency"
                placeholder={FORM_MESSAGES.originalAmount.label}
                formik={formik}
                messages={FORM_MESSAGES}
                disabled={formik.isSubmitting}
                margin="none"
                onChange={e => {
                  const { value } = e.target;
                  formik.setFieldTouched('originalAmount', true);
                  formik.setFieldValue('originalAmount', value, true);

                  if (formik.values.recurrence > 0) {
                    formik.setFieldValue(
                      'installmentAmount',
                      Number((Number(value) / Number(formik.values.recurrence)).toFixed(2)),
                    );
                  }
                }}
              />
              {formik.values.paymentPlan && formik.values.paymentPlan !== 'custom' && (
                <TextField
                  required
                  name="dueDay"
                  type="number"
                  placeholder={FORM_MESSAGES.dueDay.label}
                  formik={formik}
                  messages={FORM_MESSAGES}
                  disabled={formik.isSubmitting}
                  margin="none"
                  inputProps={{ min: 1, max: 30 }}
                />
              )}
              {formik.values.paymentPlan && formik.values.paymentPlan !== 'custom' && (
                <Mui.FormControlLabel
                  control={
                    <Mui.Switch
                      color="primary"
                      checked={!!formik.values.dueDaySelectionEnabled}
                      onChange={event => {
                        formik.setFieldTouched('dueDaySelectionEnabled', true);
                        formik.setFieldValue('dueDaySelectionEnabled', event.target.checked);
                      }}
                      disabled={formik.isSubmitting}
                    />
                  }
                  label={FORM_MESSAGES.dueDaySelectionEnabled.label}
                />
              )}
              {formik.values.paymentPlan && formik.values.paymentPlan !== 'custom' && (
                <Mui.FormControl variant="outlined" fullWidth disabled={formik.isSubmitting}>
                  <Mui.InputLabel id="billingStartDateTypeLabel">
                    {FORM_MESSAGES.billingStartDateType.label}
                  </Mui.InputLabel>
                  <Mui.Select
                    labelId="billingStartDateTypeLabel"
                    label={FORM_MESSAGES.billingStartDateType.label}
                    id="billingStartDateType"
                    value={formik.values.billingStartDateType}
                    disabled={formik.isSubmitting}
                    onChange={event => {
                      const { value } = event.target;
                      formik.setFieldTouched('billingStartDateType', true);
                      formik.setFieldValue('billingStartDateType', value);
                      if (value !== 'fixed') {
                        formik.setFieldTouched('billingStartDate', false);
                        formik.setFieldValue('billingStartDate', '');
                      }
                    }}>
                    <Mui.MenuItem value="fixed">Data fixa</Mui.MenuItem>
                    <Mui.MenuItem value="enrollment_creation_date">
                      Data da criação da matrícula
                    </Mui.MenuItem>
                  </Mui.Select>
                </Mui.FormControl>
              )}
              {showBillingStartDate && (
                <TextField
                  required
                  name="billingStartDate"
                  envelope="date"
                  formik={formik}
                  messages={FORM_MESSAGES}
                  disabled={formik.isSubmitting}
                  margin="none"
                />
              )}
            </Stack>

            <Mui.Card variant="outlined">
              <Mui.CardHeader title={<Mui.Typography variant="h5">Taxas</Mui.Typography>} />
              <Mui.CardContent>
                <Stack
                  layout={[
                    { xs: 12 },
                    { xs: 12, sm: 6 },
                    { xs: 12 },
                    { xs: 12, sm: 6 },
                    { xs: 12, sm: 6 },
                  ]}>
                  <Mui.Typography variant="h6">Juros mensal</Mui.Typography>
                  <TextField
                    name="monthlyInterestRate"
                    envelope="percentage"
                    formik={formik}
                    messages={FORM_MESSAGES}
                    disabled={formik.isSubmitting}
                    margin="none"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Mui.Typography variant="h6">Multa por atraso</Mui.Typography>
                  <TextField
                    name="latePaymentFinePercentage"
                    envelope="percentage"
                    formik={formik}
                    messages={FORM_MESSAGES}
                    disabled={formik.isSubmitting}
                    margin="none"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <TextField
                    name="latePaymentFineFixed"
                    envelope="currency"
                    formik={formik}
                    messages={FORM_MESSAGES}
                    disabled={formik.isSubmitting}
                    margin="none"
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Stack>
              </Mui.CardContent>
            </Mui.Card>
            <Mui.Card variant="outlined">
              <Mui.CardHeader
                title={
                  <Mui.Typography variant="h5" id="payableWithLabel">
                    {FORM_MESSAGES.payableWith.label}
                  </Mui.Typography>
                }
              />
              <Mui.CardContent>
                <Stack>
                  <Stack
                    layout={{ xs: 'auto' }}
                    spacing={3}
                    role="group"
                    aria-labelledby="payableWithLabel"
                    alignItems="center">
                    <Mui.FormControlLabel
                      style={{ margin: 0 }}
                      control={
                        <Mui.Switch
                          color="primary"
                          checked={formik.values.payableWith.includes('billing_note')}
                          onChange={event =>
                            handleCheckPayableWith('billing_note', event.target.checked)
                          }
                          disabled={formik.isSubmitting}
                        />
                      }
                      label="Boleto bancário"
                    />
                    <Mui.FormControlLabel
                      style={{ margin: 0 }}
                      control={
                        <Mui.Switch
                          color="primary"
                          checked={formik.values.payableWith.includes('credit_card')}
                          onChange={event =>
                            handleCheckPayableWith('credit_card', event.target.checked)
                          }
                          disabled={formik.isSubmitting}
                        />
                      }
                      label="Cartão de crédito"
                    />
                    <Mui.FormControlLabel
                      style={{ margin: 0 }}
                      control={
                        <Mui.Switch
                          color="primary"
                          checked={formik.values.payableWith.includes('pix')}
                          onChange={event => handleCheckPayableWith('pix', event.target.checked)}
                          disabled={formik.isSubmitting}
                        />
                      }
                      label="Pix"
                    />
                  </Stack>
                  {showMaxInstallments && (
                    <Stack layout={{ xs: 12, sm: 6 }}>
                      <TextField
                        required
                        name="maxInstallments"
                        type="number"
                        formik={formik}
                        messages={FORM_MESSAGES}
                        disabled={formik.isSubmitting}
                        margin="none"
                      />
                    </Stack>
                  )}
                </Stack>
              </Mui.CardContent>
            </Mui.Card>
          </Stack>
        )}
        {showStep(2) && (
          <Stack>
            {!isEditView && (
              <Mui.Box>
                <Mui.Typography variant={isSm ? 'h5' : 'h4'}>
                  Deseja oferecer desconto para quem pagar em dia?
                </Mui.Typography>
                <Mui.Typography variant="body1">
                  Configure os descontos de pontualidade para os pagamentos realizados dentro do
                  prazo.
                </Mui.Typography>
              </Mui.Box>
            )}
            {!isEditView &&
              !showDiscounts &&
              !!CardSelector &&
              CardSelector({
                options: [
                  { key: 'yes', title: 'Sim' },
                  { key: 'no', title: 'Não' },
                ],
                selectedOptionKey: showDiscounts ? 'yes' : 'no',
                onSelect: option => {
                  if (option.key === 'yes') {
                    setDiscountsEnabled(true);
                  } else {
                    handleNext();
                  }
                },
              })}
            {formik.submitCount > 0 && formik.errors.earlyPaymentDiscounts && (
              <MuiLab.Alert severity="error">
                Revise os dias e valores dos descontos de pontualidade.
              </MuiLab.Alert>
            )}
            {showDiscounts && (
              <EarlyPaymentDiscounts
                formik={formik}
                formMessages={FORM_MESSAGES}
                values={values}
                onRemoveDiscount={onRemoveDiscount}
              />
            )}
          </Stack>
        )}
        {showStep(3) &&
          !!PaymentsView &&
          PaymentsView({
            data: values,
            isEditing: isEditView,
            onSkipChargeTax: !isEditView ? handleNext : undefined,
          })}
        {showStep(4) && !!ProductsView && ProductsView({ data: values, isEditing: isEditView })}
      </Section>
      <Mui.Box display="flex" justifyContent="space-between" gridGap={10}>
        {!isEditView && step > 0 ? (
          <Mui.Button
            color="secondary"
            variant="outlined"
            size="large"
            disabled={formik.isSubmitting}
            startIcon={<Mui.Icon>chevron_left</Mui.Icon>}
            onClick={() => setStep(step - 1)}>
            Voltar
          </Mui.Button>
        ) : (
          <Mui.Box />
        )}
        <Mui.Box display="flex" gridGap={10}>
          {showSubmitButton && (
            <Mui.Button
              color="primary"
              variant="contained"
              size="large"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting || (!isEditView && (!formik.isValid || !formik.dirty))}
              onClick={formik.handleSubmit}>
              {values ? 'Salvar' : 'Cadastrar'}
            </Mui.Button>
          )}
          {!isEditView && step < steps.length - 1 && (
            <Mui.Button
              color="primary"
              variant="outlined"
              size="large"
              disabled={!checkStepFilled()}
              endIcon={<Mui.Icon>chevron_right</Mui.Icon>}
              onClick={handleNext}>
              Próximo
            </Mui.Button>
          )}
          {!isEditView && step === steps.length - 1 && (
            <Mui.Button
              color="primary"
              variant="outlined"
              size="large"
              endIcon={<Mui.Icon>check_all</Mui.Icon>}
              onClick={onFinish}>
              Finalizar
            </Mui.Button>
          )}
        </Mui.Box>
      </Mui.Box>
    </Stack>
  );
}

Create.propTypes = {
  isEditView: PropTypes.bool,
  values: PropTypes.object,
  onFinish: PropTypes.func,
  onRemoveDiscount: PropTypes.func,
  onSubmit: PropTypes.func,
  CardSelector: PropTypes.func,
  CycleSelect: PropTypes.func,
  PaymentsView: PropTypes.func,
  ProductsView: PropTypes.func,
};

export default Create;
